import React, { useState, useEffect, useRef } from "react";
import UserItem from "./userItem.jsx";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import { inviteUser } from "../../api/admin.js";
import { addToast } from "../../store/app/action.js";
import { useDispatch, useSelector } from "react-redux";
import CanView from "../../components/CanView.jsx";


function UsersTable({ users, storeID, orgID, refetch, storeName, isAdmin }) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contextOpenedFor, setContextOpenedFor] = useState(null);
  const dispatch = useDispatch();
  const selectRef = useRef(null);
  const checkboxRef = useRef(null);
  const { selectedMerchant } = useSelector((state) => state.app);

  const handleRoleChange = () => {
    if (checkboxRef.current && selectRef.current) {
      checkboxRef.current.disabled = selectRef.current.value !== "admin";
    }
  };
  return (
    <div className="py-4">
      {/* Invite User Modal */}
      <ModalBasic
        width={"md"}
        hideCloseButton
        modalOpen={showInviteModal}
        title={isAdmin ? "Invite Admin" : "Invite User"}
        setModalOpen={() => {
          setShowInviteModal(false);
        }}
      >
        {showInviteModal && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              const email = formData.get("email").trim().toLowerCase();
              const role = formData.get("role");
              const first_name = formData.get("first_name");
              const last_name = formData.get("last_name");
              const billing = formData.get("billing");

              if (isAdmin) {
                if (
                  !email.endsWith("@saucey.com") &&
                  !email.endsWith("@saucey.it") &&
                  !email.endsWith("@sauceydirect.com")
                ) {
                  dispatch(
                    addToast({
                      type: "error",
                      message:
                        "Email must end in @saucey.com, @saucey.it or @sauceydirect.com",
                    })
                  );
                  return;
                }
              }

              const p = [role];
              if (billing) {
                p.push("billing");
              }
              const permissions = isAdmin
                ? null
                : [
                    {
                      merchant: orgID,
                      locations: [
                        {
                          location: storeID,
                          permissions: p,
                        },
                      ],
                      type: "user",
                    },
                  ];

              try {
                setLoading(true);
                await inviteUser({
                  first_name,
                  last_name,
                  email,
                  permissions,
                  store_name: storeName,
                  admin: isAdmin,
                  direct: selectedMerchant.direct,
                });
                refetch();
                setShowInviteModal(false);
                dispatch(
                  addToast({
                    type: "success",
                    message: "User invited successfully",
                    timer: 5,
                  })
                );
              } catch (e) {
                let message = "An error occurred";
                try {
                  const serverMessage = e.response?.data?.error;
                  if (serverMessage) {
                    message = serverMessage;
                  }
                } catch {}
                message += ", updates are NOT saved.";
                dispatch(
                  addToast({
                    type: "error",
                    message: message,
                  })
                );
              } finally {
                setLoading(false);
              }
            }}
          >
            <div className="space-y-4">
              <div>
                <div className="py-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="first_name"
                  >
                    First Name
                  </label>
                  <input
                    disabled={loading}
                    name="first_name"
                    className="form-input w-full"
                    type="text"
                  />
                </div>

                <div className="py-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="last_name"
                  >
                    Last Name
                  </label>

                  <input
                    disabled={loading}
                    name="last_name"
                    className="form-input w-full"
                    type="text"
                  />
                </div>

                <div className="py-1">
                  <label
                    className="block text-sm font-medium mb-1"
                    htmlFor="email"
                  >
                    Email Address
                  </label>
                  <input
                    disabled={loading}
                    name="email"
                    className="form-input w-full"
                    type="email"
                  />
                </div>
              </div>
              <div>
                {/*    drop down for roles: admin, manager, clerk*/}
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="role"
                >
                  Role
                </label>
                <select
                  disabled={loading || isAdmin}
                  ref={selectRef}
                  onChange={handleRoleChange}
                  id="role"
                  name="role"
                  className="form-select w-full"
                >
                  {isAdmin && <option value="admin">Super Admin</option>}
                  {!isAdmin && (
                    <CanView permissions="admin">
                      <option value="admin">Admin</option>
                    </CanView>
                  )}
                  {!isAdmin && <option value="manager">Manager</option>}
                  {!isAdmin && <option value="clerk">Clerk</option>}
                </select>
                {/*checkbox (With Billing)*/}
                {!isAdmin && (
                  <div className="py-1">
                    <div className="flex items-center">
                      <input
                        id="billing"
                        name="billing"
                        type="checkbox"
                        ref={checkboxRef}
                        className="form-checkbox"
                      />
                      <label htmlFor="billing" className="ml-2">
                        With Billing Permission
                      </label>
                    </div>
                  </div>
                )}
                <div className="pt-6 flex gap-2">
                  <SecondaryButton
                    type={"reset"}
                    onClick={(e) => {
                      setShowInviteModal(false);
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type={"submit"}>Invite User</PrimaryButton>
                </div>
              </div>
            </div>
          </form>
        )}
      </ModalBasic>
      <PrimaryButton
        onClick={() => {
          setShowInviteModal(true);
        }}
      >
        {isAdmin ? "Invite Admin" : "Invite User"}
      </PrimaryButton>
      <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative !mb-48 mt-4">
        <div>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="table-auto w-full dark:text-slate-300">
              {/* Table header */}
              <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Name</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Email</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Status</div>
                  </th>
                  {!isAdmin && (
                    <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                      <div className="font-semibold text-left">Permissions</div>
                    </th>
                  )}
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left"></div>
                  </th>
                </tr>
              </thead>
              {/* Table body */}
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {(users || []).map((user) => {
                  return (
                    <UserItem
                      key={user._id}
                      user={user}
                      storeID={storeID}
                      refetch={refetch}
                      setContextOpenedFor={setContextOpenedFor}
                      contextOpenedFor={contextOpenedFor}
                      isAdmin={isAdmin}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsersTable;
